/**
 * Open a first-party Losi path from a realtime session.
 *
 * LiveKit data packets arrive off a user gesture, so browsers commonly
 * block window.open. Always offer a toast action so one click completes
 * the open when the popup is blocked.
 */

import { toast } from 'sonner'

export type OpenRealtimePageOptions = {
  /** Default true — keep the mic tab, open the surface elsewhere. */
  newTab?: boolean
  /** Same-tab / split navigation when newTab is false. */
  onNavigateInApp?: (path: string) => void
  label?: string
}

function safeAbsoluteUrl(path: string): string | null {
  const trimmed = path.trim()
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null
  if (typeof window === 'undefined') return null
  try {
    return new URL(trimmed, window.location.origin).toString()
  } catch {
    return null
  }
}

export function openRealtimeLosiPage(
  path: string,
  options: OpenRealtimePageOptions = {}
): boolean {
  const newTab = options.newTab !== false
  const label = options.label || path
  const url = safeAbsoluteUrl(path)
  if (!url) {
    toast.error('Could not open that Losi page')
    return false
  }

  if (!newTab) {
    if (options.onNavigateInApp) {
      options.onNavigateInApp(path.trim())
      toast.success(`Opened ${label}`)
      return true
    }
    try {
      window.location.assign(url)
      return true
    } catch {
      toast.error('Could not open that Losi page')
      return false
    }
  }

  let opened: Window | null = null
  try {
    opened = window.open(url, '_blank', 'noopener,noreferrer')
  } catch {
    opened = null
  }

  if (opened) {
    toast.success('Opened in a new tab', {
      description: label,
      action: {
        label: 'Open again',
        onClick: () => {
          window.open(url, '_blank', 'noopener,noreferrer')
        }
      }
    })
    return true
  }

  // Popup blocked (common for async LiveKit data events). User click works.
  toast.message(`Open ${label}`, {
    description: 'Browser blocked the popup — click Open.',
    duration: 20_000,
    action: {
      label: 'Open',
      onClick: () => {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    }
  })
  return false
}
